import { twMerge } from "tailwind-merge";
import { AnimatePresence, motion } from "motion/react";
import { LoaderBase, type LoaderBaseProps } from "@core/ui";

export type Props = LoaderBaseProps & {
  /** Additional CSS classes for the spinner SVG */
  innerClassName?: string;
  /** Whether to display as full page loader */
  isFullPage?: boolean;
  /** Whether to use absolute positioning */
  isAbsolute?: boolean;
};

const Wrapper = ({ children }: { children: React.ReactNode }) => (
  <AnimatePresence>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="flex flex-1 h-full w-full justify-center items-center self-center"
    >
      {children}
    </motion.div>
  </AnimatePresence>
);

/**
 * Styled loader component with animation and layout variants.
 * Wraps LoaderBase from @core/ui with framer-motion animations.
 */
export const Loader = ({
  className,
  innerClassName,
  isFullPage = false,
  isAbsolute = false,
  "aria-label": ariaLabel = "Loading",
  ...props
}: Props) => {
  const SpinnerSvg = () => (
    <svg
      className={twMerge("text-brand-600 h-28 w-28 animate-spin", innerClassName)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 32 32"
    >
      <path
        d="M8 16a8 8 0 0 1 16 0"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );

  const Loading = () => (
    <LoaderBase
      className={twMerge("flex items-center justify-center", className)}
      aria-label={ariaLabel}
      renderSpinner={() => <SpinnerSvg />}
      {...props}
    />
  );

  if (isAbsolute) {
    return (
      <Wrapper>
        <div className="absolute flex z-50 justify-center py-8 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <Loading />
        </div>
      </Wrapper>
    );
  }

  if (isFullPage) {
    return (
      <Wrapper>
        <div className="flex flex-1 h-full w-full justify-center items-center self-center">
          <Loading />
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Loading />
    </Wrapper>
  );
};

export default Loader;

